/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { createUseStyles } from 'react-jss';
import ReactModal from 'react-modal';

import styles, { fadeDuration } from './AccessibleModal.style';

import { modalId } from 'src/shared/app/base/constant/domConstants';

type Props = {
  children: React.Node,
  label?: string,
  contentClass?: string,
  overlayClass?: string,
  forceClose?: boolean,
  handleAfterOpen?: () => void,
  handleAfterClose?: () => void,
  handleRequestClose?: () => void,
};

const useStyles = createUseStyles(styles);

const AccessibleModal = ({
  children,
  label,
  contentClass,
  overlayClass,
  forceClose,
  handleAfterOpen,
  handleAfterClose,
  handleRequestClose,
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | State
   |----------------------------------------------------------------------------
   */

  const [isOpen, setOpen] = React.useState(true);

  /*
   |----------------------------------------------------------------------------
   | Effects
   |----------------------------------------------------------------------------
   */

  React.useEffect(() => {
    if (forceClose) {
      setOpen(false);
    }
  }, [forceClose]);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onRequestClose = () => {
    setOpen(false);

    if (handleRequestClose) {
      handleRequestClose();
    }
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <ReactModal
      className={contentClass}
      isOpen={isOpen}
      contentLabel={label}
      closeTimeoutMS={fadeDuration}
      htmlOpenClassName={classes.html}
      bodyOpenClassName={classes.body}
      overlayClassName={{
        base: classNames(classes.overlay, overlayClass),
        afterOpen: classes['overlay-afterOpen'],
        beforeClose: classes['overlay-beforeClose'],
      }}
      parentSelector={() => document.getElementById(modalId)}
      onAfterOpen={handleAfterOpen}
      onAfterClose={handleAfterClose}
      onRequestClose={onRequestClose}
    >
      {children}
    </ReactModal>
  );
};

export default compose(React.memo)(AccessibleModal);
